const pickImageUrl = (urlObj) => {
    if (urlObj == null) return null;
    if (typeof urlObj === 'string' && urlObj.trim() !== '') return urlObj.trim();
    if (typeof urlObj !== 'object') return null;
    return (
        urlObj.normal ||
        urlObj.original ||
        urlObj.medium ||
        urlObj.large ||
        urlObj.small ||
        urlObj.thumbnail ||
        urlObj.url ||
        urlObj.href ||
        urlObj.src ||
        null
    );
};

/**
 * Caminhos extras onde a API pode mandar URL de arquivo de imagem.
 */
const pickLooseImageString = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const keys = ['path', 'full_url', 'public_url', 'file_url', 'cdn_url', 'permalink'];
    for (const k of keys) {
        const v = obj[k];
        if (typeof v === 'string' && v.trim() !== '') return v.trim();
    }
    return null;
};

function findSubproductById(obj, id, depth = 0) {
    if (obj == null || depth > 8) return null;
    if (typeof obj !== 'object') return null;
    if (obj.id === id && (Object.prototype.hasOwnProperty.call(obj, 'sku') || Object.prototype.hasOwnProperty.call(obj, 'color_id') || Object.prototype.hasOwnProperty.call(obj, 'product_id'))) {
        return obj;
    }
    if (Array.isArray(obj.subproducts)) {
        const hit = obj.subproducts.find((s) => s && s.id === id);
        if (hit) return hit;
    }
    for (const key of Object.keys(obj)) {
        const child = obj[key];
        if (!child || typeof child !== 'object') continue;
        if (Array.isArray(child)) {
            if (key === 'subproducts') {
                const hit = child.find((s) => s && s.id === id);
                if (hit) return hit;
            }
            continue;
        }
        const hit = findSubproductById(child, id, depth + 1);
        if (hit) return hit;
    }
    return null;
}

const pickGalleryRowUrl = (imageRow) => {
    if (!imageRow) return null;
    return pickImageUrl(imageRow.image_url) || pickLooseImageString(imageRow);
};

/**
 * Usa `product.images` quando o subproduto só traz `product_image_id` ou nome de arquivo sem URLs.
 */
const resolveVariantImageFromParentGallery = (subproduct, parentProduct) => {
    if (!subproduct || !parentProduct?.images?.length) return null;

    const imageId = subproduct.product_image_id ?? subproduct.image?.id;
    if (imageId) {
        const row = parentProduct.images.find((im) => im && im.id === imageId);
        const u = pickGalleryRowUrl(row);
        if (u) return u;
    }

    const fname =
        subproduct.image && typeof subproduct.image === 'object' && typeof subproduct.image.image === 'string'
            ? subproduct.image.image.trim()
            : null;
    if (fname) {
        const base = fname.split('/').pop();
        const row = parentProduct.images.find(
            (im) =>
                im &&
                typeof im.image === 'string' &&
                (im.image === fname || im.image.split('/').pop() === base)
        );
        const u = pickGalleryRowUrl(row);
        if (u) return u;
    }

    return null;
};

/**
 * Só imagem vinculada ao subproduto (cor), sem cair no fallback do produto pai.
 * `parentProduct` habilita resolver via galeria `images` + product_image_id / nome de arquivo.
 */
export const getSubproductVariantOwnImageUrl = (subproduct, parentProduct) => {
    if (!subproduct) return null;

    const fromNested = pickImageUrl(subproduct.image?.image_url);
    if (fromNested) return fromNested;

    const fromGallery = resolveVariantImageFromParentGallery(subproduct, parentProduct);
    if (fromGallery) return fromGallery;

    if (subproduct.image && typeof subproduct.image === 'object' && !subproduct.image.image_url) {
        const loose = pickLooseImageString(subproduct.image) || pickImageUrl(subproduct.image);
        if (loose) return loose;
    }

    if (typeof subproduct.image === 'string' && subproduct.image.trim() !== '') {
        return subproduct.image.trim();
    }

    if (subproduct.product_image && typeof subproduct.product_image === 'object') {
        const u = pickImageUrl(subproduct.product_image.image_url) || pickLooseImageString(subproduct.product_image);
        if (u) return u;
    }

    if (Array.isArray(subproduct.images) && subproduct.images.length > 0) {
        const first = subproduct.images[0];
        const u = pickImageUrl(first?.image_url) || pickImageUrl(first) || pickLooseImageString(first);
        if (u) return u;
    }

    return (
        pickImageUrl(subproduct.subproduct_image_url) ||
        pickImageUrl(subproduct.image_url) ||
        pickLooseImageString(subproduct) ||
        null
    );
};

/**
 * URL de preview da variante (cor) — subproduto, depois imagens gerais do produto.
 */
export const getSubproductPreviewImageUrl = (subproduct, parentProduct) => {
    if (!subproduct) return null;

    const own = getSubproductVariantOwnImageUrl(subproduct, parentProduct);
    if (own) return own;

    const images = parentProduct?.images ?? subproduct.product?.images;
    if (images?.length > 0 && images[0].image_url) {
        return pickImageUrl(images[0].image_url);
    }

    return null;
};

/**
 * URLs da galeria de imagens do produto (`product.images`), na ordem da API.
 */
export const getProductGalleryImageUrls = (parentProduct) => {
    if (!Array.isArray(parentProduct?.images)) return [];
    return parentProduct.images.map((row) => pickGalleryRowUrl(row)).filter(Boolean);
};

/**
 * Indica se vale buscar GET /product/:id para preencher URLs que faltam no payload atual.
 * Qualquer variante sem preview resolvido (nem via galeria) dispara o fetch — evita cor sem imagem
 * quando o search manda subproduto sem `image_url` nem “dicas” (evidência: debug H-B, sub 8cfe52ab).
 */
export const productNeedsSubproductImageDetail = (parentProduct) => {
    if (!parentProduct?.id || !Array.isArray(parentProduct.subproducts) || parentProduct.subproducts.length === 0) {
        return false;
    }
    return parentProduct.subproducts.some((sp) => sp && !getSubproductPreviewImageUrl(sp, parentProduct));
};

/**
 * URL para miniatura no select de produto (prefere small/thumbnail do payload).
 */
export const getSubproductSelectThumbnailUrl = (subproduct, parentProduct) => {
    if (!subproduct) return null;
    const nested = subproduct.image?.image_url;
    if (nested && typeof nested === 'object') {
        const s = nested.small || nested.thumbnail || nested.medium;
        if (typeof s === 'string' && s.trim() !== '') return s.trim();
    }
    const own = getSubproductVariantOwnImageUrl(subproduct, parentProduct);
    if (own) return own;
    return getSubproductPreviewImageUrl(subproduct, parentProduct);
};

/**
 * Extrai o objeto subproduto (para preview) da resposta GET /subproduct/:id/search.
 * `subproductId` permite achar a variante em estruturas aninhadas (product.subproducts, data, etc.).
 */
export const extractSubproductDetailFromSearchResource = (resource, subproductId) => {
    const r = resource && typeof resource === 'object' ? resource : {};
    const nested =
        r.subproduct ??
        r.sub_product ??
        r.variant ??
        (r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data.subproduct ?? r.data : null);

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return nested;
    }

    if (subproductId != null) {
        const found = findSubproductById(r, subproductId);
        if (found) return found;
    }

    if (r.id == null) return null;
    if (Array.isArray(r.prices)) return r;
    if (getSubproductVariantOwnImageUrl(r, r.product)) return r;
    return null;
};

/** Mockup ou URL que não deve ser exibida como <img>. */
export const isLikelyNonImageMockupSrc = (src) => {
    if (!src || typeof src !== 'string') return false;
    const lower = src.split('?')[0].toLowerCase();
    if (lower.endsWith('.pdf')) return true;
    if (src.startsWith('data:application/pdf')) return true;
    return false;
};
